import type {
  Character,
  CombatStats,
  EquippedItem,
  PlayerWithCharacters,
} from '@moussenaires/shared';
import {
  bestAvailableParsePercent,
  bestRaidParseSummary,
  CLASS_COLORS,
  formatCompactNumber,
  itemLevelColor,
  mythicPlusScoreColor,
  parseColor,
  RELATIVE_RANK_COLORS,
  relativeRankTier,
  WCL_DIFFICULTY_IDS,
} from '@moussenaires/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  Check,
  Crosshair,
  Crown,
  Droplet,
  Dumbbell,
  Heart,
  Info,
  Loader2,
  MousePointerClick,
  Pause,
  Pencil,
  Play,
  Plus,
  Shield,
  Wind,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { mediaSrc } from '../api/client.js';
import { ROSTER_SYNC_PROGRESS_KEY, type RosterSyncProgress } from '../hooks/useRealtime.js';
import { BrandIcon } from './BrandIcon.js';
import { FactionIcon } from './ClassBadge.js';
import { ItemPopover } from './ItemPopover.js';
import type { RosterSortMode } from './RosterSortToggle.js';
import './RosterHeroWall.css';

interface HeroEntry {
  character: Character;
  playerId: number;
  playerName: string;
}

interface EmptyPlayer {
  id: number;
  name: string;
}

/**
 * Where the Armory's own paperdoll puts each slot: armor (and the main-hand
 * weapon) down the left, accessories (and the off-hand) down the right —
 * the real layout the character panel uses, not a guess.
 */
const LEFT_COLUMN = [
  'HEAD',
  'NECK',
  'SHOULDER',
  'BACK',
  'CHEST',
  'SHIRT',
  'TABARD',
  'WRIST',
  'MAIN_HAND',
];
const RIGHT_COLUMN = [
  'HANDS',
  'WAIST',
  'LEGS',
  'FEET',
  'FINGER_1',
  'FINGER_2',
  'TRINKET_1',
  'TRINKET_2',
  'OFF_HAND',
  'RANGED',
];

/** Which icon to use for whichever of Strength/Agility/Intellect turned out to be this character's primary stat. */
const PRIMARY_STAT_ICON: Record<string, LucideIcon> = {
  Strength: Dumbbell,
  Agility: Wind,
  Intellect: Brain,
};

/**
 * A mythic parse always outranks a heroic one, even a 40% mythic against a
 * 90% heroic — reaching the harder difficulty at all means killing a boss
 * the heroic-only character never touched. So the difficulty comes first
 * (as a dominant order of magnitude) and the percent only breaks ties
 * within the same difficulty.
 */
function sortValue(character: Character, mode: RosterSortMode): number {
  if (mode === 'mythicPlus') {
    return character.enrichment?.mythicPlus?.score ?? character.stats.mythicPlusScore ?? -1;
  }
  const bestParse = bestRaidParseSummary(character.enrichment?.raidParses ?? null);
  if (!bestParse) return -1;
  const percent = bestAvailableParsePercent(bestParse) ?? 0;
  return WCL_DIFFICULTY_IDS[bestParse.difficulty] * 1000 + percent;
}

/**
 * Picks "10+" out of the translated label ("Clés 10+", "10+ keys" — the
 * word's position differs per locale) and colors just that part green, so
 * it reads as "timed", the same way a timed key glows green in-game —
 * independent of whatever count color the number itself gets.
 */
function highlightTenPlus(label: string): ReactNode {
  const index = label.indexOf('10+');
  if (index === -1) return label;
  return (
    <>
      {label.slice(0, index)}
      <span className="hero-card-stat-timed">10+</span>
      {label.slice(index + '10+'.length)}
    </>
  );
}

/**
 * There's no fixed "good" number of 10+ keys — only standing relative to
 * whoever else is on this roster right now, so the color comes from rank
 * among everyone currently shown, not a fixed threshold: lowest green,
 * highest "shiny gold". Ties (equal counts) always share a color, since
 * they're computed by rank among DISTINCT values, not per character.
 */
function tenPlusKeyColorsById(entries: HeroEntry[]): Map<number, string> {
  const counts = entries
    .map((entry) => entry.character.enrichment?.mythicPlus?.tenPlusKeystoneCount ?? null)
    .filter((count): count is number => count !== null);
  const distinctSorted = [...new Set(counts)].sort((a, b) => a - b);
  const colorByCount = new Map(
    distinctSorted.map((count, rank) => [
      count,
      RELATIVE_RANK_COLORS[relativeRankTier(rank, distinctSorted.length)],
    ]),
  );

  const colorsById = new Map<number, string>();
  for (const entry of entries) {
    const count = entry.character.enrichment?.mythicPlus?.tenPlusKeystoneCount ?? null;
    const color = count === null ? undefined : colorByCount.get(count);
    if (color) colorsById.set(entry.character.id, color);
  }
  return colorsById;
}

/**
 * The main roster view: one full-height column per character, split evenly
 * across the page. Hovering does nothing — clicking a character is what
 * focuses it: the column expands and reveals its gear ring and outbound
 * links, and a "Details" button appears for the full stats/logs modal.
 * Clicking the focused card again (or a different one) moves the focus.
 * Admin CRUD (edit/delete/sync/logs) lives behind that Details button;
 * player-level editing rides along on the small pencil next to the
 * character's name, since a hero card is per-character.
 */
export function RosterHeroWall({
  players,
  isAdmin,
  sortMode,
  onSelectCharacter,
  onAddCharacterFor,
  onEditPlayer,
}: {
  players: PlayerWithCharacters[];
  isAdmin: boolean;
  sortMode: RosterSortMode;
  onSelectCharacter: (characterId: number) => void;
  onAddCharacterFor: (playerId: number) => void;
  onEditPlayer: (playerId: number) => void;
}) {
  const { t } = useTranslation();
  const [focusedId, setFocusedId] = useState<number | null>(null);
  // Set (with a short self-clearing safety net, never a listener of its own)
  // right when a gear item's popover is dismissed by an outside click — see
  // HeroCard's use of it. Purely local to this wall; touches nothing else
  // on the page, unlike a document-level listener would.
  const suppressNextToggleRef = useRef(false);
  const entries: HeroEntry[] = players
    .flatMap((player) =>
      player.characters.map((character) => ({
        character,
        playerId: player.id,
        playerName: player.name,
      })),
    )
    .sort((a, b) => sortValue(b.character, sortMode) - sortValue(a.character, sortMode));
  const emptyPlayers: EmptyPlayer[] = isAdmin
    ? players.filter((player) => player.characters.length === 0)
    : [];
  const tenPlusKeyColors = tenPlusKeyColorsById(entries);

  if (entries.length === 0 && emptyPlayers.length === 0) {
    return <p className="center-state">{t('common.empty')}</p>;
  }

  return (
    <div className="hero-wall">
      {entries.map(({ character, playerId, playerName }, index) => (
        <HeroCard
          key={character.id}
          character={character}
          playerName={playerName}
          cardIndex={index}
          isAdmin={isAdmin}
          isFocused={focusedId === character.id}
          tenPlusKeyColor={tenPlusKeyColors.get(character.id) ?? null}
          onToggleFocus={() => {
            if (suppressNextToggleRef.current) {
              suppressNextToggleRef.current = false;
              return;
            }
            setFocusedId((current) => (current === character.id ? null : character.id));
          }}
          suppressNextToggleRef={suppressNextToggleRef}
          onOpenDetails={() => onSelectCharacter(character.id)}
          onEditPlayer={() => onEditPlayer(playerId)}
        />
      ))}
      {emptyPlayers.map((player) => (
        <button
          key={player.id}
          type="button"
          className="hero-card hero-card-ghost"
          onClick={() => onAddCharacterFor(player.id)}
        >
          <Plus size={28} />
          <span>{player.name}</span>
          <span className="hero-card-ghost-hint">{t('roster.addCharacter')}</span>
        </button>
      ))}
    </div>
  );
}

function slotPosition(slot: string): { side: 'left' | 'right'; index: number } | null {
  const left = LEFT_COLUMN.indexOf(slot);
  if (left !== -1) return { side: 'left', index: left };
  const right = RIGHT_COLUMN.indexOf(slot);
  if (right !== -1) return { side: 'right', index: right };
  return null;
}

interface CombatStatEntry {
  icon: LucideIcon;
  color: string;
  value: number;
  suffix: string;
  label: string;
}

function combatStatEntries(stats: CombatStats): CombatStatEntry[] {
  const entries: CombatStatEntry[] = [];
  if (stats.health !== null) {
    entries.push({ icon: Heart, color: '#4ade80', value: stats.health, suffix: '', label: 'HP' });
  }
  if (stats.power !== null) {
    entries.push({
      icon: Droplet,
      color: '#38bdf8',
      value: stats.power,
      suffix: '',
      label: stats.powerLabel ?? '',
    });
  }
  if (stats.primaryStat !== null) {
    entries.push({
      icon: (stats.primaryStatLabel && PRIMARY_STAT_ICON[stats.primaryStatLabel]) || Brain,
      color: '#a78bfa',
      value: stats.primaryStat,
      suffix: '',
      label: stats.primaryStatLabel ?? '',
    });
  }
  if (stats.stamina !== null) {
    entries.push({
      icon: Shield,
      color: '#fb923c',
      value: stats.stamina,
      suffix: '',
      label: 'STA',
    });
  }
  if (stats.criticalStrikePercent !== null) {
    entries.push({
      icon: Crosshair,
      color: '#f87171',
      value: stats.criticalStrikePercent,
      suffix: '%',
      label: 'CRIT',
    });
  }
  if (stats.hastePercent !== null) {
    entries.push({
      icon: Zap,
      color: '#4ade80',
      value: stats.hastePercent,
      suffix: '%',
      label: 'HASTE',
    });
  }
  if (stats.masteryPercent !== null) {
    entries.push({
      icon: Crown,
      color: '#a78bfa',
      value: stats.masteryPercent,
      suffix: '%',
      label: 'MASTERY',
    });
  }
  if (stats.versatilityPercent !== null) {
    entries.push({
      icon: Wrench,
      color: '#f59e0b',
      value: stats.versatilityPercent,
      suffix: '%',
      label: 'VERS',
    });
  }
  return entries;
}

/**
 * Same shape and count as a fully-synced character's real combat stats
 * (see `combatStatEntries` — in practice they're either all 8 present or
 * none, since they all come off one Blizzard API response) but rendered
 * invisible. Reserving the grid's exact footprint before the data exists
 * means the real values fading in later only crossfades content in place
 * — nothing above or below it (the realm name, the rest of the bottom bar)
 * has to reflow out from under a block that used to be absent.
 */
const COMBAT_STAT_PLACEHOLDERS: CombatStatEntry[] = Array.from({ length: 8 }, (_, index) => ({
  icon: Heart,
  color: 'transparent',
  value: 0,
  suffix: '',
  label: String(index),
}));

function HeroCard({
  character,
  playerName,
  cardIndex,
  isAdmin,
  isFocused,
  tenPlusKeyColor,
  onToggleFocus,
  suppressNextToggleRef,
  onOpenDetails,
  onEditPlayer,
}: {
  character: Character;
  playerName: string;
  cardIndex: number;
  isAdmin: boolean;
  isFocused: boolean;
  /** Rank-relative among the whole roster, not a fixed threshold — see `tenPlusKeyColorsById`. */
  tenPlusKeyColor: string | null;
  onToggleFocus: () => void;
  suppressNextToggleRef: RefObject<boolean>;
  onOpenDetails: () => void;
  onEditPlayer: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as 'fr' | 'en';
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [openItem, setOpenItem] = useState<{ item: EquippedItem; anchorRect: DOMRect } | null>(
    null,
  );
  const armory = character.enrichment?.armory ?? null;
  const imageUrl = armory?.renderUrl ?? armory?.avatarUrl ?? null;
  const classColor = character.class ? CLASS_COLORS[character.class] : 'var(--text-muted)';
  const mythicPlusScore =
    character.enrichment?.mythicPlus?.score ?? character.stats.mythicPlusScore;
  // The real season total — see `fetchTenPlusKeystoneCount` in raiderIo.ts.
  const tenPlusKeyCount = character.enrichment?.mythicPlus?.tenPlusKeystoneCount ?? null;
  const bestParse = bestRaidParseSummary(character.enrichment?.raidParses ?? null);
  const parsePercent = bestAvailableParsePercent(bestParse);
  const equipment = armory?.equipment ?? [];
  const combatStats = armory?.combatStats ? combatStatEntries(armory.combatStats) : [];

  const armoryUrl = armory?.profileUrl ?? character.profileUrl;
  const wclUrl = bestParse?.profileUrl ?? character.enrichment?.raidParses?.[0]?.profileUrl ?? null;
  const raiderIoUrl = character.enrichment?.mythicPlus?.profileUrl ?? null;

  // Purely client-local, push-driven state (see useRealtime's handling of
  // `roster.syncStarted`/`characterSynced`) — there's nothing to actually
  // fetch, so `initialData` seeds it synchronously (no async round-trip
  // that a push arriving before it resolves could race and lose to).
  const syncState = useQuery({
    queryKey: ROSTER_SYNC_PROGRESS_KEY,
    queryFn: () => ({}) as RosterSyncProgress,
    initialData: () => ({}) as RosterSyncProgress,
    staleTime: Infinity,
    gcTime: Infinity,
    select: (progress) => progress[character.id],
  }).data;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`hero-card ${isFocused ? 'hero-card-focused' : ''}`}
      style={{
        ['--hero-class-color' as string]: classColor,
        ['--card-index' as string]: cardIndex,
      }}
      onClick={onToggleFocus}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggleFocus();
        }
      }}
    >
      {isFocused && character.media?.kind === 'video' ? (
        <video
          key={character.media.id}
          ref={videoRef}
          src={mediaSrc(character.media.url)}
          className="hero-card-image"
          autoPlay
          loop
          muted
          playsInline
          onPlay={() => setIsVideoPlaying(true)}
          onPause={() => setIsVideoPlaying(false)}
        />
      ) : isFocused && character.media ? (
        <img
          key={character.media.id}
          src={mediaSrc(character.media.url)}
          alt=""
          className="hero-card-image"
        />
      ) : imageUrl ? (
        <img src={mediaSrc(imageUrl)} alt="" className="hero-card-image" loading="lazy" />
      ) : (
        <div className="hero-card-placeholder">{character.name.slice(0, 2).toUpperCase()}</div>
      )}
      <span className="hero-card-scrim hero-card-scrim-top" aria-hidden="true" />
      <span className="hero-card-scrim hero-card-scrim-bottom" aria-hidden="true" />

      {/* A pure hover nudge — "yes, this is clickable" — never shown once
          the card is already focused (it's obviously interactive by then). */}
      {!isFocused && (
        <span className="hero-card-hover-hint" aria-hidden="true">
          <MousePointerClick size={20} />
        </span>
      )}

      {/* Top bar: always-visible headline numbers, clear of the floating nav. */}
      <span className="hero-card-top">
        <span className="hero-card-name-row">
          <FactionIcon faction={character.faction} />
          <span className="hero-card-name" style={{ color: classColor }}>
            {character.name}
          </span>
          {syncState === 'pending' && (
            <Loader2
              size={13}
              className="hero-card-sync-indicator hero-card-sync-spinner"
              aria-label={t('roster.syncing')}
              role="status"
            />
          )}
          {syncState === 'done' && (
            <Check
              size={13}
              className="hero-card-sync-indicator hero-card-sync-check"
              aria-label={t('roster.syncComplete')}
              role="status"
            />
          )}
          {isFocused && character.media?.kind === 'video' && (
            <button
              type="button"
              className="hero-card-video-toggle"
              aria-label={isVideoPlaying ? t('roster.pauseClip') : t('roster.playClip')}
              title={isVideoPlaying ? t('roster.pauseClip') : t('roster.playClip')}
              onClick={(event) => {
                event.stopPropagation();
                if (isVideoPlaying) videoRef.current?.pause();
                else void videoRef.current?.play();
              }}
            >
              {isVideoPlaying ? <Pause size={11} /> : <Play size={11} />}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="hero-card-player-edit"
              aria-label={t('roster.editPlayer') + ` (${playerName})`}
              title={t('roster.editPlayer') + ` (${playerName})`}
              onClick={(event) => {
                event.stopPropagation();
                onEditPlayer();
              }}
            >
              <Pencil size={11} />
            </button>
          )}
        </span>
        {!character.class && <span className="hero-card-syncing">{t('roster.notSyncedYet')}</span>}
        <span className="hero-card-stats">
          {armory?.itemLevel != null && (
            <span className="hero-card-stat" style={{ color: itemLevelColor(armory.itemLevel) }}>
              <strong>{Math.round(armory.itemLevel)}</strong>
              <small>{t('roster.itemLevel')}</small>
            </span>
          )}
          {parsePercent !== null && bestParse && (
            <span className="hero-card-stat" style={{ color: parseColor(parsePercent) }}>
              <strong>{parsePercent}%</strong>
              <small>{t(`roster.difficulty.${bestParse.difficulty}`)}</small>
            </span>
          )}
          {mythicPlusScore !== null && (
            <span
              className="hero-card-stat"
              style={{ color: mythicPlusScoreColor(mythicPlusScore) }}
            >
              <strong>{Math.round(mythicPlusScore)}</strong>
              <small>{t('roster.mythicPlusScore')}</small>
            </span>
          )}
          {tenPlusKeyCount !== null && (
            <span className="hero-card-stat" style={{ color: tenPlusKeyColor ?? undefined }}>
              <strong>{tenPlusKeyCount}</strong>
              <small>{highlightTenPlus(t('roster.tenPlusKeys'))}</small>
            </span>
          )}
        </span>
      </span>

      {/* Gear ring: only once focused, hugging the render like the Armory paperdoll. */}
      {isFocused &&
        equipment.map((item) => {
          const position = slotPosition(item.slot);
          if (!position) return null;
          return (
            <button
              key={item.slot}
              type="button"
              className={`hero-card-gear-item hero-card-gear-${position.side}`}
              style={{ ['--gear-index' as string]: position.index }}
              title={item.name}
              onClick={(event) => {
                event.stopPropagation();
                setOpenItem({ item, anchorRect: event.currentTarget.getBoundingClientRect() });
              }}
            >
              {item.iconUrl ? (
                <img src={mediaSrc(item.iconUrl)} alt="" loading="lazy" />
              ) : (
                <span className="hero-card-gear-item-placeholder" />
              )}
              {item.gems?.length > 0 && (
                <span className="hero-card-gear-item-gem" aria-hidden="true" />
              )}
            </button>
          );
        })}

      {/* Bottom bar: realm, the Armory-style stat block, and (once focused) outbound links + details. */}
      <span className="hero-card-bottom">
        <span className="hero-card-subline">{character.realm}</span>

        {/* Always mounted, even before the Armory sync resolves — see
            COMBAT_STAT_PLACEHOLDERS above for why: reserving its footprint
            up front means real values arriving later never reflow anything
            else in this bottom bar out from under itself. */}
        <span className="hero-card-combat-stats" aria-hidden={combatStats.length === 0}>
          {(combatStats.length > 0 ? combatStats : COMBAT_STAT_PLACEHOLDERS).map((entry) => (
            <span
              key={entry.label}
              className={`hero-card-combat-stat ${combatStats.length === 0 ? 'hero-card-combat-stat-placeholder' : ''}`}
            >
              <entry.icon size={13} style={{ color: entry.color }} />
              <strong>
                {formatCompactNumber(entry.value, locale)}
                {entry.suffix}
              </strong>
              <small>{entry.label}</small>
            </span>
          ))}
        </span>

        {isFocused && (
          <span className="hero-card-links">
            {armoryUrl && (
              <a
                href={armoryUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                <BrandIcon site="armory" size={13} /> {t('roster.viewOnArmory')}
              </a>
            )}
            {wclUrl && (
              <a
                href={wclUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                <BrandIcon site="warcraftlogs" size={13} /> {t('roster.viewOnWarcraftLogs')}
              </a>
            )}
            {raiderIoUrl && (
              <a
                href={raiderIoUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
              >
                <BrandIcon site="raiderio" size={13} /> {t('roster.viewOnRaiderIo')}
              </a>
            )}
            <button
              type="button"
              className="hero-card-details"
              onClick={(event) => {
                event.stopPropagation();
                onOpenDetails();
              }}
            >
              <Info size={12} /> {t('roster.details')}
            </button>
          </span>
        )}
      </span>

      {openItem && (
        <ItemPopover
          item={openItem.item}
          anchorRect={openItem.anchorRect}
          onClose={() => setOpenItem(null)}
          onOutsideDismiss={() => {
            suppressNextToggleRef.current = true;
            // Safety net only — normally consumed the moment the dismiss
            // click's own follow-up click reaches a card's onToggleFocus.
            window.setTimeout(() => {
              suppressNextToggleRef.current = false;
            }, 300);
          }}
        />
      )}
    </div>
  );
}
